import type { FieldDefinition, IntegrationProviderConfig } from '../../types';
import WordPressIcon from './WordPressIcon';

const pullObjectTypes = [
  { label: 'Posts', value: 'posts' },
  { label: 'Pages', value: 'pages' },
  { label: 'Media', value: 'media' },
  { label: 'Categories', value: 'categories' },
  { label: 'Tags', value: 'tags' },
  { label: 'Comments', value: 'comments' },
  { label: 'Users', value: 'users' },
];

const orderByOptionsPostsPages = [
  { label: 'Date', value: 'date' },
  { label: 'Modified', value: 'modified' },
  { label: 'Title', value: 'title' },
  { label: 'Relevance', value: 'relevance' },
  { label: 'ID', value: 'id' },
  { label: 'Slug', value: 'slug' },
];

const orderByOptionsMedia = [
  { label: 'Date', value: 'date' },
  { label: 'Modified', value: 'modified' },
  { label: 'Title', value: 'title' },
  { label: 'ID', value: 'id' },
  { label: 'Slug', value: 'slug' },
];

const orderByOptionsTerms = [
  { label: 'Name', value: 'name' },
  { label: 'ID', value: 'id' },
  { label: 'Count', value: 'count' },
  { label: 'Slug', value: 'slug' },
  { label: 'Description', value: 'description' },
];

const orderByOptionsComments = [
  { label: 'Date', value: 'date' },
  { label: 'ID', value: 'id' },
  { label: 'Type', value: 'type' },
];

const orderByOptionsUsers = [
  { label: 'Name', value: 'name' },
  { label: 'ID', value: 'id' },
  { label: 'Registered Date', value: 'registered_date' },
  { label: 'Slug', value: 'slug' },
  { label: 'Email', value: 'email' },
];

function getPullFields(objectType: string): FieldDefinition[] {
  const base: FieldDefinition[] = [
    {
      name: 'perPage',
      label: 'Per Page',
      type: 'number',
      defaultValue: 10,
      placeholder: 'Default 10, Max 100',
      validation: { maxLength: 3 },
      hasConnectionPoint: true,
    },
    {
      name: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Free-text search query',
      hasConnectionPoint: true,
    },
  ];

  if (objectType === 'posts' || objectType === 'pages') {
    return [
      {
        name: 'statusFilter',
        label: 'Status Filter',
        type: 'select',
        options: 'dynamic',
        hasConnectionPoint: true,
      },
      {
        name: 'categoryFilter',
        label: 'Category Filter',
        type: 'multiselect',
        options: 'dynamic',
        hasConnectionPoint: true,
      },
      {
        name: 'tagFilter',
        label: 'Tag Filter',
        type: 'multiselect',
        options: 'dynamic',
        hasConnectionPoint: true,
      },
      {
        name: 'authorFilter',
        label: 'Author Filter',
        type: 'select',
        options: 'dynamic',
        hasConnectionPoint: true,
      },
      {
        name: 'dateFrom',
        label: 'Date From',
        type: 'date',
        hasConnectionPoint: true,
      },
      {
        name: 'dateTo',
        label: 'Date To',
        type: 'date',
        hasConnectionPoint: true,
      },
      ...base,
      {
        name: 'orderBy',
        label: 'Order By',
        type: 'select',
        options: orderByOptionsPostsPages,
        hasConnectionPoint: true,
      },
    ];
  }

  if (objectType === 'media') {
    return [
      {
        name: 'dateFrom',
        label: 'Date From',
        type: 'date',
        hasConnectionPoint: true,
      },
      {
        name: 'dateTo',
        label: 'Date To',
        type: 'date',
        hasConnectionPoint: true,
      },
      {
        name: 'mediaType',
        label: 'Media Type',
        type: 'select',
        options: [
          { label: 'Image', value: 'image' },
          { label: 'Video', value: 'video' },
          { label: 'Audio', value: 'audio' },
          { label: 'Application', value: 'application' },
        ],
        hasConnectionPoint: true,
      },
      ...base,
      {
        name: 'orderBy',
        label: 'Order By',
        type: 'select',
        options: orderByOptionsMedia,
        hasConnectionPoint: true,
      },
    ];
  }

  if (objectType === 'categories' || objectType === 'tags') {
    return [
      ...base,
      {
        name: 'orderBy',
        label: 'Order By',
        type: 'select',
        options: orderByOptionsTerms,
        hasConnectionPoint: true,
      },
    ];
  }

  if (objectType === 'comments') {
    return [
      {
        name: 'dateFrom',
        label: 'Date From',
        type: 'date',
        hasConnectionPoint: true,
      },
      {
        name: 'dateTo',
        label: 'Date To',
        type: 'date',
        hasConnectionPoint: true,
      },
      ...base,
      {
        name: 'orderBy',
        label: 'Order By',
        type: 'select',
        options: orderByOptionsComments,
        hasConnectionPoint: true,
      },
    ];
  }

  if (objectType === 'users') {
    return [
      ...base,
      {
        name: 'orderBy',
        label: 'Order By',
        type: 'select',
        options: orderByOptionsUsers,
        hasConnectionPoint: true,
      },
    ];
  }

  return base;
}

const pushActions = [
  { label: 'Create Post', value: 'create_post' },
  { label: 'Update Post', value: 'update_post' },
  { label: 'Create Page', value: 'create_page' },
  { label: 'Update Page', value: 'update_page' },
  { label: 'Upload Media', value: 'upload_media' },
];

function getPushFields(action: string): FieldDefinition[] {
  const base: FieldDefinition[] = [];

  if (
    action === 'create_post' ||
    action === 'update_post' ||
    action === 'create_page' ||
    action === 'update_page'
  ) {
    const isPost = action === 'create_post' || action === 'update_post';
    const fields: FieldDefinition[] = [
      {
        name: 'title',
        label: 'Title',
        type: 'text',
        placeholder: 'Post or page title',
        validation: { maxLength: 200 },
        hasConnectionPoint: true,
      },
      {
        name: 'content',
        label: 'Content',
        type: 'textarea',
        placeholder: 'HTML or Markdown',
        hasConnectionPoint: true,
      },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select Status',
        hasConnectionPoint: true,
      },
      ...(isPost
        ? [
            {
              name: 'categories',
              label: 'Categories',
              type: 'multiselect' as const,
              options: 'dynamic' as const,
              placeholder: 'Select Categories',
              hasConnectionPoint: true,
            },
            {
              name: 'tags',
              label: 'Tags',
              type: 'multiselect' as const,
              options: 'dynamic' as const,
              placeholder: 'Select Tags',
              hasConnectionPoint: true,
            },
          ]
        : []),
      {
        name: 'author',
        label: 'Author',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select Author',
        hasConnectionPoint: true,
      },
      {
        name: 'slug',
        label: 'Slug',
        type: 'text',
        placeholder: 'URL slug (optional)',
        hasConnectionPoint: true,
      },
      {
        name: 'featuredImageUrl',
        label: 'Featured Image URL',
        type: 'text',
        placeholder: 'URL of image',
        hasConnectionPoint: true,
      },
    ];
    if (action === 'update_post' || action === 'update_page') {
      fields.unshift({
        name: 'postId',
        label: 'Post/Page ID',
        type: 'text',
        placeholder: 'ID of post or page to update',
        validation: { required: true },
        hasConnectionPoint: true,
      });
    }
    return fields;
  }

  if (action === 'upload_media') {
    return [
      {
        name: 'fileUrl',
        label: 'File URL',
        type: 'text',
        placeholder: 'URL of file to upload',
        hasConnectionPoint: true,
      },
      {
        name: 'altText',
        label: 'Alt Text',
        type: 'text',
        placeholder: 'Alt text for image',
        hasConnectionPoint: true,
      },
    ];
  }

  return base;
}

export const wordPressConfig: IntegrationProviderConfig = {
  id: 'wordpress',
  name: 'WordPress',
  icon: WordPressIcon,
  brandColor: '#21759B',
  authMethod: 'oauth2',
  authFields: [
    {
      name: 'siteUrl',
      label: 'Site URL',
      type: 'text',
      placeholder: 'https://yourwebsite.com',
      validation: { required: true },
    },
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      placeholder: 'WordPress username',
      validation: { required: true },
    },
    {
      name: 'applicationPassword',
      label: 'Application Password',
      type: 'text',
      placeholder: 'Application password from WP user profile',
      validation: { required: true },
    },
  ],
  pullConfig: {
    objectTypes: pullObjectTypes,
    getFields: getPullFields,
  },
  pushConfig: {
    actions: pushActions,
    getFields: getPushFields,
  },
  supportsPull: true,
  supportsPush: true,
};
